import React from "react";
import { Box, Grid, Typography, Link } from "@mui/material";
import { Instagram, Email } from "@mui/icons-material";
import colorConfigs from "../../../configs/colorConfigs";
import { typo } from "../../../assets/fonts/font";
import { useNavigate } from "react-router-dom";
import { PATH_NAME } from "../../../configs/pathName";
import useSWR from "swr";
import { getCollections } from "../../../api/CollectionAPI";
import assets from "../../../assets";
import { getCurrentBrand } from "../../../configs/brandConfigs";

const Footer: React.FC = () => {
  const brand = getCurrentBrand();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  // Fetch all collections
  const { data: collectionsData, error: collectionsError } = useSWR(
    "getCollections",
    getCollections
  );

  if (collectionsError) {
    console.error("Failed to fetch collections:", collectionsError);
  }

  return (
    <Box sx={{ background: colorConfigs.secondary, paddingTop: "40px" }}>
      <Box
        sx={{
          backgroundColor: colorConfigs.primary,
          color: colorConfigs.footerText,
          border: "1px solid var(--color-black)",
          padding: "40px 20px",
          borderRadius: "20px 20px 0 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          overflow: "hidden",
          "& .MuiTypography-root, & .MuiLink-root, & .MuiSvgIcon-root": {
            color: colorConfigs.footerText,
          },
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{ 
            textAlign: "left", marginBottom: "20px" }}
        >
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", color: colorConfigs.footerText }}>
              <Typography sx={{ ...typo.title }}>{brand?.name}</Typography>
              <Typography 
                sx={{ ...typo.bodyContent, cursor: "pointer" }} 
                onClick={() => navigate(PATH_NAME.ALLPRODUCTS)}
              >
                All Products
              </Typography>
              {/* Dynamically render collection links */}
              {collectionsData?.map((collection: any) => (
                <Typography
                  key={collection.id}
                  sx={{ ...typo.bodyContent, cursor: "pointer" }}
                  onClick={() => navigate(`/collections/${collection.slug}`)}
                >
                  {collection.name}
                </Typography>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", color: colorConfigs.footerText }}>
              <Typography sx={{ ...typo.title }}>Smart Fridge</Typography>
              <Typography
                sx={{ ...typo.bodyContent, cursor: "pointer" }}
                onClick={() => {
                  navigate(PATH_NAME.SMART_FRIDGE);
                  setTimeout(() => {
                    const contactForm = document.getElementById("contact-form");
                    if (contactForm) {
                      contactForm.scrollIntoView({ behavior: "smooth" });
                    }
                  }, 100);
                }}
              >
                Contact Us
              </Typography>
              {/* Legal section below Smart Fridge */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: 3 }}>
                <Typography sx={{ ...typo.title }}>Legal</Typography>
                <Typography
                  sx={{ ...typo.bodyContent, cursor: "pointer" }}
                  onClick={() => navigate(PATH_NAME.PRIVACY_POLICY)}
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" },
              alignItems: "center",
              flexDirection: "column",
              color: colorConfigs.footerText,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", sm: "flex-end" },
              }}
            >
              <Link href="https://play.google.com/store/apps/details?id=com.nommi.club" target="_blank" color="inherit">
                <img src={assets.images.GooglePlayStore} alt="Google PlayStore" width={150}/>
              </Link>
              <Link href="https://apps.apple.com/my/app/nommi-club/id6744875110" target="_blank" color="inherit">
                <img src={assets.images.AppStore} alt="App Store" width={150}/>
              </Link>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "20px",
              }}
            >
              <Link
                href="https://www.instagram.com/nomzy.club/"
                target="_blank"
                color="inherit"
              >
                <Instagram fontSize="large" />
              </Link>
              <Link href="https://www.tiktok.com/@nomzy.club" target="_blank" color="inherit">
                <img
                  src={assets.images.TikTok}
                  alt="TikTok"
                  width={32}
                  height={32}
                  style={{ filter: brand?.id === "nomzy" ? "brightness(0)" : "none" }}
                />
              </Link>
              <Link href="mailto:hello@nomzy.club" target="_blank" color="inherit">
                <Email fontSize="large" />
              </Link>
            </Box>
          </Grid>
        </Grid>
        <Typography sx={{ ...typo.bodyContent, color: colorConfigs.footerText }} align="center">
          &copy; {currentYear}{" "}
          <Box
            component="span"
            sx={{
              fontWeight: "bold",
              display: "inline-flex",
              fontSize: "inherit",
            }}
          >
            {brand?.name}
          </Box>
          . All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;